import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { cartUtils, inr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CreditCard, Truck } from "lucide-react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CheckoutPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", line1: "", city: "", state: "", pincode: "" });
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [distanceKm, setDistanceKm] = useState("");
  const [autoDistance, setAutoDistance] = useState(null); // { pincode, distance_km, to } | null
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [payCfg, setPayCfg] = useState({ cod_max_order_inr: 30000, delivery_base_fee: 100, delivery_base_range_km: 20, delivery_step_km: 5, delivery_step_fee: 50 });
  const [placing, setPlacing] = useState(false);
  const [isFirstOrder, setIsFirstOrder] = useState(false);
  const [firstOrderPct, setFirstOrderPct] = useState(5);

  useEffect(() => {
    setItems(cartUtils.get());
    if (!user) nav("/login?next=/checkout");
    else setForm((f) => ({ ...f, name: user.name || "", phone: user.phone || "" }));
    api.get("/payment/config").then((r) => {
      setPayCfg(r.data);
      if (typeof r.data.first_order_discount_pct === "number") setFirstOrderPct(r.data.first_order_discount_pct);
    }).catch(() => {});
    // Check if this will be the customer's first order
    if (user) {
      api.get("/orders/my").then((r) => setIsFirstOrder((r.data || []).length === 0)).catch(() => {});
    }
  }, [user, nav]);

  // Auto-lookup distance whenever a valid 6-digit pincode is entered
  useEffect(() => {
    const pin = (form.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) { setAutoDistance(null); return; }
    let cancelled = false;
    setDistanceLoading(true);
    api.get(`/geo/distance?pincode=${pin}`)
      .then((r) => {
        if (cancelled) return;
        setAutoDistance(r.data);
        setDistanceKm(String(r.data.distance_km));
      })
      .catch(() => { if (!cancelled) setAutoDistance(null); })
      .finally(() => { if (!cancelled) setDistanceLoading(false); });
    return () => { cancelled = true; };
  }, [form.pincode]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const discount = isFirstOrder ? Math.round(subtotal * firstOrderPct / 100) : 0;
  const discountedSubtotal = subtotal - discount;
  const codEligible = discountedSubtotal <= payCfg.cod_max_order_inr;

  // Universal delivery charge — applies to BOTH online & COD
  const deliveryCharge = useMemo(() => {
    const km = parseFloat(distanceKm);
    if (!km || km <= 0) return payCfg.delivery_base_fee;
    if (km <= payCfg.delivery_base_range_km) return payCfg.delivery_base_fee;
    const extra = km - payCfg.delivery_base_range_km;
    const steps = Math.ceil(extra / payCfg.delivery_step_km);
    return payCfg.delivery_base_fee + steps * payCfg.delivery_step_fee;
  }, [distanceKm, payCfg]);

  const total = discountedSubtotal + deliveryCharge;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty");
    if (!form.line1 || !form.city || !form.pincode) return toast.error("Please complete your address");
    const km = parseFloat(distanceKm);
    if (!km || km <= 0) return toast.error("We need your delivery distance to calculate delivery charges. Please enter a valid pincode or km.");
    if (paymentMethod === "cod" && !codEligible) return toast.error(`COD is available only for orders up to ${inr(payCfg.cod_max_order_inr)}. Please pay online.`);
    setPlacing(true);
    try {
      const r = await api.post("/orders/create", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, price: i.price, name: i.name, image: i.image })),
        shipping_address: form,
        payment_method: paymentMethod,
        delivery_distance_km: km,
      });
      const { order_id, amount, razorpay_order_id, razorpay_key_id, payment_method } = r.data;

      if (payment_method === "cod") {
        cartUtils.clear();
        toast.success("Order placed! Our team will call you to confirm & schedule delivery.");
        nav(`/order-success/${order_id}?method=cod`);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Payment gateway failed to load"); setPlacing(false); return; }

      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount,
        currency: "INR",
        name: "Vardhman Furniture",
        description: `Order ${order_id.slice(0, 8)}`,
        order_id: razorpay_order_id,
        prefill: { name: form.name, email: user.email, contact: form.phone },
        theme: { color: "#1C1917" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            cartUtils.clear();
            toast.success("Payment successful");
            nav(`/order-success/${order_id}?method=online`);
          } catch (e) {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Checkout</h1>
      <form onSubmit={submit} className="mt-10 grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2 space-y-6">
          <div className="space-y-4">
            <p className="eyebrow">Shipping Address</p>
            <input data-testid="checkout-name" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input data-testid="checkout-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <input data-testid="checkout-address" placeholder="Address Line" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} required />
            <div className="grid grid-cols-3 gap-4">
              <input data-testid="checkout-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
              <input data-testid="checkout-state" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              <input data-testid="checkout-pincode" placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} required />
            </div>
          </div>

          {/* Delivery — auto-calculated for BOTH payment methods */}
          <div data-testid="delivery-card" className="border border-stone-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <Truck size={20} strokeWidth={1.5} className="text-[#8B5A2B] mt-0.5"/>
              <div className="flex-1">
                <p className="text-xs tracking-widest uppercase text-stone-500">Delivery</p>
                <h3 className="font-serif text-xl mt-1">Distance-based delivery from our Bathinda store</h3>
                <p className="mt-1 text-xs text-stone-500">₹{payCfg.delivery_base_fee} up to {payCfg.delivery_base_range_km} km · +₹{payCfg.delivery_step_fee} for every additional {payCfg.delivery_step_km} km. Applies to all payment methods.</p>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
                  <div>
                    <label className="text-[10px] tracking-widest uppercase text-stone-500">Distance from Bathinda (km)</label>
                    <input
                      data-testid="checkout-distance-km"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="e.g. 15"
                      value={distanceKm}
                      onChange={(e) => setDistanceKm(e.target.value)}
                      className="mt-1 w-40 block"
                      required
                    />
                  </div>
                  {distanceLoading && <span className="text-xs text-stone-500 mb-2">Looking up pincode…</span>}
                </div>
                {autoDistance && !distanceLoading && (
                  <p data-testid="delivery-auto-distance" className="mt-2 text-xs text-stone-600">
                    Auto-estimated from pincode <span className="font-medium">{autoDistance.pincode}</span>: ~{autoDistance.distance_km} km. You can adjust above if you have a better estimate.
                  </p>
                )}
                {distanceKm && parseFloat(distanceKm) > 0 && (
                  <p data-testid="delivery-charge-preview" className="mt-2 text-sm">
                    <span className="text-stone-500">Delivery charge:</span> <span className="font-medium text-[#8B5A2B]">{inr(deliveryCharge)}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="eyebrow">Payment Method</p>
            <label
              data-testid="pay-method-online"
              className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors ${paymentMethod === "online" ? "border-[#8B5A2B] bg-[#FAF6EF]" : "border-stone-200 hover:border-stone-400"}`}
            >
              <input type="radio" name="pm" className="mt-1" checked={paymentMethod === "online"} onChange={() => setPaymentMethod("online")} />
              <div className="flex-1">
                <div className="flex items-center gap-2"><CreditCard size={18} strokeWidth={1.5} /><span className="font-medium text-stone-900">Pay Online (Razorpay)</span></div>
                <p className="mt-1 text-xs text-stone-500">UPI, cards, netbanking & wallets. Secure payment powered by Razorpay.</p>
              </div>
            </label>

            <label
              data-testid="pay-method-cod"
              className={`flex items-start gap-3 border p-4 transition-colors ${!codEligible ? "opacity-50 cursor-not-allowed border-stone-200" : `cursor-pointer ${paymentMethod === "cod" ? "border-[#8B5A2B] bg-[#FAF6EF]" : "border-stone-200 hover:border-stone-400"}`}`}
            >
              <input type="radio" name="pm" className="mt-1" disabled={!codEligible} checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} />
              <div className="flex-1">
                <div className="flex items-center gap-2"><Truck size={18} strokeWidth={1.5} /><span className="font-medium text-stone-900">Cash on Delivery</span></div>
                {codEligible ? (
                  <p className="mt-1 text-xs text-stone-500">Pay in cash when your order arrives. Available for orders up to {inr(payCfg.cod_max_order_inr)}.</p>
                ) : (
                  <p className="mt-1 text-xs text-red-600">COD not available — order total exceeds {inr(payCfg.cod_max_order_inr)}. Please pay online.</p>
                )}
              </div>
            </label>
          </div>
        </div>

        <div>
          <div className="bg-white border border-stone-200 p-6">
            <p className="eyebrow">Order Summary</p>
            <ul className="mt-4 divide-y divide-stone-200">
              {items.map((i) => (
                <li key={i.product_id} className="py-2 flex justify-between text-sm">
                  <span>{i.name} × {i.quantity}</span>
                  <span>{inr(i.price * i.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-stone-200 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{inr(subtotal)}</span></div>
              {isFirstOrder && discount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>First-order welcome ({firstOrderPct}% off)</span>
                  <span data-testid="checkout-discount">- {inr(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-600">
                <span>Delivery charge{distanceKm && parseFloat(distanceKm) > 0 ? ` (${distanceKm} km)` : ""}</span>
                <span data-testid="checkout-delivery">{inr(deliveryCharge)}</span>
              </div>
              <div className="flex justify-between font-serif text-lg pt-2 border-t border-stone-200"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
            </div>
            <button data-testid="checkout-pay-btn" disabled={placing} type="submit" className="btn-primary w-full mt-6 disabled:opacity-60">
              {placing ? "Processing…" : paymentMethod === "cod" ? "Place COD Order" : "Pay with Razorpay"}
            </button>
            <p className="mt-3 text-xs text-stone-500">
              {paymentMethod === "cod"
                ? "Our team will call to confirm your order before dispatch."
                : "Secure payments via Razorpay. UPI, cards, netbanking accepted."}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
